"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EvmCore = exports.EvmContract = void 0;
const vm_1 = __importDefault(require("@ethereumjs/vm"));
const ethereumjs_util_1 = require("ethereumjs-util");
const abi_1 = require("@ethersproject/abi");
const index_1 = require("../index");
const bnHex_helper_1 = require("../../helpers/bnHex.helper");
const abi_helper_1 = require("../../helpers/abi.helper");
const method_does_not_exist_exception_1 = require("./exceptions/method-does-not-exist.exception");
const wrong_params_passed_to_method_exception_1 = require("./exceptions/wrong-params-passed-to-method.exception");
const wrong_amount_of_arguments_exception_1 = require("./exceptions/wrong-amount-of-arguments.exception");
const is_valid_1 = require("./validators/is-valid");
const wrong_param_type_exception_1 = require("./exceptions/wrong-param-type.exception");
class EvmContract {
    evm;
    abi;
    address;
    code;
    constructor(vmc, address, abi, code) {
        this.address = address;
        this.evm = vmc;
        this.abi = abi;
        this.code = code;
    }
    static async build(evmCore, address, abi) {
        const code = await evmCore.network.loadScCode(address);
        return new EvmContract(evmCore, address, abi, code);
    }
    async scGet(method, params) {
        if (!this.isMethodExist(method)) {
            throw new method_does_not_exist_exception_1.MethodDoesNotExistException();
        }
        if (!this.isValidParamsPassedToMethod(method, params)) {
            throw new wrong_params_passed_to_method_exception_1.WrongParamsPassedToMethodException();
        }
        const io = (0, abi_helper_1.getAbiInputsOutputsType)(this.abi, method);
        const encodedFunction = (0, abi_helper_1.encodeFunction)(method, params, io.inputs);
        const contractAddress = ethereumjs_util_1.Address.fromString(index_1.AddressApi.textAddressToEvmAddress(this.address));
        const getResult = await this.evm.vm.runCall({
            to: contractAddress,
            data: Buffer.from(encodedFunction, 'hex'),
            code: this.code,
        });
        if (getResult.execResult.exceptionError) {
            throw getResult.execResult.exceptionError;
        }
        const results = abi_1.defaultAbiCoder.decode(io.outputs, getResult.execResult.returnValue);
        let returnValue = results;
        if (io.outputNames.length === results.length) {
            returnValue = results.reduce((aggr, item, key) => {
                aggr[io.outputNames[key]] = item;
                return aggr;
            }, {});
        }
        return results.length === 1 ? results[0] : returnValue;
    }
    async scSet(key, method, params, amount = 0, isHTTPSNodesOnly = false, sponsor = '') {
        if (!this.isMethodExist(method)) {
            throw new method_does_not_exist_exception_1.MethodDoesNotExistException();
        }
        if (!this.isValidParamsPassedToMethod(method, params)) {
            throw new wrong_params_passed_to_method_exception_1.WrongParamsPassedToMethodException();
        }
        const addressChain = await this.evm.network.getAddressChain(key.address);
        const addressChainNumber = addressChain?.chain;
        let workNetwork = this.evm.network;
        if (this.evm.network.getChain() !== addressChainNumber) {
            workNetwork = new index_1.NetworkApi(addressChainNumber);
            await workNetwork.bootstrap(isHTTPSNodesOnly);
        }
        const io = (0, abi_helper_1.getAbiInputsOutputsType)(this.abi, method);
        const encodedFunction = (0, abi_helper_1.encodeFunction)(method, params, io.inputs);
        const data = Buffer.from(encodedFunction, 'hex');
        const tx = sponsor === '' ?
            await index_1.TransactionsApi.composeSCMethodCallTX(key.address, this.address, ['0x0', [data]], '', 0, key.wif, 'SK', amount, workNetwork.feeSettings, workNetwork.gasSettings) :
            await index_1.TransactionsApi.composeSponsorSCMethodCallTX(key.address, this.address, ['0x0', [data]], '', 0, key.wif, 'SK', amount, workNetwork.feeSettings, workNetwork.gasSettings, sponsor);
        const res = await workNetwork.sendTxAndWaitForResponse(tx);
        return res;
    }
    isMethodExist(method) {
        return !!this.abi.find((item) => item.name === method);
    }
    isValidParamsPassedToMethod(method, params = []) {
        const { inputs } = (0, abi_helper_1.getAbiInputsOutputs)(this.abi, method);
        if (params.length !== inputs.length) {
            throw new wrong_amount_of_arguments_exception_1.WrongAmountOfArgumentsException(method, inputs.length, params.length);
        }
        for (let i = 0; i < inputs.length; i += 1) {
            const input = inputs[i];
            const param = params[i];
            if (!(0, is_valid_1.isValid)(param, input.type, input.components)) {
                throw new wrong_param_type_exception_1.WrongParamTypeException(method, input.name);
            }
        }
        return true;
    }
}
exports.EvmContract = EvmContract;
class EvmCore {
    vm;
    network;
    constructor(vm, network) {
        this.network = network;
        this.vm = vm;
    }
    static async build(network) {
        const vm = await vm_1.default.create();
        vm.stateManager.getContractStorage = async (address, key) => {
            const val = (0, bnHex_helper_1.bnToHex)(`0x${key.toString('hex')}`);
            const state = await network.loadScStateByKey(index_1.AddressApi.hexToTextAddress(index_1.AddressApi.evmAddressToHexAddress(address.toString())), val);
            return Buffer.from(state);
        };
        return new EvmCore(vm, network);
    }
}
exports.EvmCore = EvmCore;
